const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export default class KAUL_CHILD_THEME_EventsCalendar {
    constructor(root) {
        if (!(root instanceof HTMLElement)) {
            return;
        }

        this.root = root;
        this.endpoint = root.dataset.endpoint || '';
        this.emptyMessage = root.dataset.emptyMessage || 'No events are scheduled for this month.';
        this.activeMonth = this.isValidMonth(root.dataset.initialMonth) ? root.dataset.initialMonth : this.currentMonth();
        this.selectedDate = '';
        this.cache = new Map();
        this.pending = new Map();
        this.requestToken = 0;
        this.lastTrigger = null;
        this.mobileMedia = window.matchMedia('(max-width: 991px)');

        this.monthLabel = root.querySelector('[data-calendar-month-label]');
        this.monthTrigger = root.querySelector('[data-calendar-picker-open]');
        this.grid = root.querySelector('[data-calendar-grid]');
        this.agendaDate = root.querySelector('[data-calendar-agenda-date]');
        this.agendaEvents = root.querySelector('[data-calendar-agenda-events]');
        this.monthEmpty = root.querySelector('[data-calendar-month-empty]');
        this.status = root.querySelector('[data-calendar-status]');
        this.dialog = root.querySelector('[data-calendar-dialog]');
        this.dialogTitle = root.querySelector('[data-calendar-dialog-title]');
        this.dialogEvents = root.querySelector('[data-calendar-dialog-events]');
        this.pickerDialog = root.querySelector('[data-calendar-picker-dialog]');
        this.pickerYear = root.querySelector('[data-calendar-picker-year]');
        this.pickerMonths = root.querySelector('[data-calendar-picker-months]');
        this.pickerTrigger = null;

        const initialData = this.readInitialData();
        if (initialData.month === this.activeMonth) {
            this.cache.set(this.activeMonth, initialData.events);
        }

        this.bindEvents();
        this.renderMonth(this.activeMonth, this.cache.get(this.activeMonth) || []);
        this.prefetchAdjacentMonths(this.activeMonth);
    }

    isValidMonth(month) {
        return typeof month === 'string' && MONTH_PATTERN.test(month);
    }

    currentMonth() {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }

    readInitialData() {
        const script = this.root.querySelector('[data-calendar-initial-data]');

        if (!(script instanceof HTMLScriptElement)) {
            return { month: this.activeMonth, events: [] };
        }

        try {
            const data = JSON.parse(script.textContent || '{}');
            return {
                month: this.isValidMonth(data.month) ? data.month : this.activeMonth,
                events: Array.isArray(data.events) ? data.events : [],
            };
        } catch (error) {
            return { month: this.activeMonth, events: [] };
        }
    }

    bindEvents() {
        this.root.addEventListener('click', (event) => {
            const previous = event.target.closest?.('[data-calendar-previous]');
            const next = event.target.closest?.('[data-calendar-next]');
            const day = event.target.closest?.('[data-calendar-date]');
            const close = event.target.closest?.('[data-calendar-dialog-close]');
            const pickerOpen = event.target.closest?.('[data-calendar-picker-open]');
            const pickerClose = event.target.closest?.('[data-calendar-picker-close]');
            const previousYear = event.target.closest?.('[data-calendar-picker-year-previous]');
            const nextYear = event.target.closest?.('[data-calendar-picker-year-next]');
            const pickerMonth = event.target.closest?.('[data-calendar-picker-month]');
            const pickerToday = event.target.closest?.('[data-calendar-picker-today]');

            if (previous) {
                this.navigate(-1);
                return;
            }

            if (next) {
                this.navigate(1);
                return;
            }

            if (day instanceof HTMLButtonElement) {
                this.selectDate(day.dataset.calendarDate || '', day);
                return;
            }

            if (close) {
                this.closeDialog();
                return;
            }

            if (pickerOpen instanceof HTMLElement) {
                this.openMonthPicker(pickerOpen);
                return;
            }

            if (pickerClose) {
                this.closeMonthPicker();
                return;
            }

            if (previousYear) {
                this.changePickerYear(-1);
                return;
            }

            if (nextYear) {
                this.changePickerYear(1);
                return;
            }

            if (pickerMonth instanceof HTMLButtonElement) {
                const year = Number(this.pickerYear?.value);
                const monthNumber = Number(pickerMonth.dataset.calendarPickerMonth);

                if (Number.isInteger(year) && Number.isInteger(monthNumber)) {
                    this.closeMonthPicker();
                    this.goToMonth(`${year}-${String(monthNumber).padStart(2, '0')}`);
                }
                return;
            }

            if (pickerToday) {
                this.closeMonthPicker();
                this.goToMonth(this.currentMonth());
            }
        });

        if (this.pickerYear instanceof HTMLSelectElement) {
            this.pickerYear.addEventListener('change', () => {
                this.renderPickerMonths(Number(this.pickerYear.value));
            });
        }

        if (this.dialog instanceof HTMLDialogElement) {
            this.dialog.addEventListener('click', (event) => {
                if (event.target === this.dialog) {
                    this.closeDialog();
                }
            });

            this.dialog.addEventListener('close', () => {
                if (this.lastTrigger instanceof HTMLElement) {
                    this.lastTrigger.focus();
                }
            });
        }

        if (this.pickerDialog instanceof HTMLDialogElement) {
            this.pickerDialog.addEventListener('click', (event) => {
                if (event.target === this.pickerDialog) {
                    this.closeMonthPicker();
                }
            });

            this.pickerDialog.addEventListener('close', () => {
                if (this.pickerTrigger instanceof HTMLElement) {
                    this.pickerTrigger.focus();
                }
            });
        }
    }

    navigate(offset) {
        const nextMonth = this.shiftMonth(this.activeMonth, offset);

        this.goToMonth(nextMonth);
    }

    goToMonth(nextMonth) {
        if (! this.isValidMonth(nextMonth)) {
            return;
        }

        const token = ++this.requestToken;

        this.setLoading(true);
        this.setStatus('Loading events…');
        this.closeDialog();

        this.fetchMonth(nextMonth)
            .then((events) => {
                if (token !== this.requestToken) {
                    return;
                }

                this.activeMonth = nextMonth;
                this.renderMonth(nextMonth, events);
                this.setStatus('');
                this.prefetchAdjacentMonths(nextMonth);
            })
            .catch(() => {
                if (token !== this.requestToken) {
                    return;
                }

                this.setStatus('Events could not be loaded. Please try again.');
            })
            .finally(() => {
                if (token === this.requestToken) {
                    this.setLoading(false);
                }
            });
    }

    openMonthPicker(trigger) {
        if (!(this.pickerDialog instanceof HTMLDialogElement)) {
            return;
        }

        const [year] = this.activeMonth.split('-').map(Number);
        this.pickerTrigger = trigger;
        this.renderPickerYearOptions(year, year);
        this.renderPickerMonths(year);

        if (typeof this.pickerDialog.showModal === 'function') {
            this.pickerDialog.showModal();
        } else {
            this.pickerDialog.setAttribute('open', '');
        }
    }

    closeMonthPicker() {
        if (!(this.pickerDialog instanceof HTMLDialogElement) || ! this.pickerDialog.open) {
            return;
        }

        if (typeof this.pickerDialog.close === 'function') {
            this.pickerDialog.close();
        } else {
            this.pickerDialog.removeAttribute('open');
        }
    }

    changePickerYear(offset) {
        if (!(this.pickerYear instanceof HTMLSelectElement)) {
            return;
        }

        const nextYear = Number(this.pickerYear.value) + offset;

        if (! Number.isInteger(nextYear)) {
            return;
        }

        if (! Array.from(this.pickerYear.options).some((option) => Number(option.value) === nextYear)) {
            this.renderPickerYearOptions(nextYear, nextYear);
        } else {
            this.pickerYear.value = String(nextYear);
        }

        this.renderPickerMonths(nextYear);
    }

    renderPickerYearOptions(centerYear, selectedYear) {
        if (!(this.pickerYear instanceof HTMLSelectElement)) {
            return;
        }

        const fragment = document.createDocumentFragment();

        for (let year = centerYear - 25; year <= centerYear + 25; year += 1) {
            const option = document.createElement('option');
            option.value = String(year);
            option.textContent = String(year);
            option.selected = year === selectedYear;
            fragment.appendChild(option);
        }

        this.pickerYear.replaceChildren(fragment);
    }

    renderPickerMonths(year) {
        if (!(this.pickerMonths instanceof HTMLElement) || ! Number.isInteger(year)) {
            return;
        }

        const activeMonthNumber = Number(this.activeMonth.split('-')[1]);
        const activeYear = Number(this.activeMonth.split('-')[0]);
        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
            month: 'short',
        });
        const fragment = document.createDocumentFragment();

        for (let monthNumber = 1; monthNumber <= 12; monthNumber += 1) {
            const button = document.createElement('button');
            button.className = 'kaul-calendar__picker-month';
            button.type = 'button';
            button.dataset.calendarPickerMonth = String(monthNumber);
            button.textContent = formatter.format(new Date(year, monthNumber - 1, 1));
            button.setAttribute('aria-label', this.formatMonth(`${year}-${String(monthNumber).padStart(2, '0')}`));

            if (year === activeYear && monthNumber === activeMonthNumber) {
                button.classList.add('is-active');
                button.setAttribute('aria-current', 'date');
            }

            fragment.appendChild(button);
        }

        this.pickerMonths.replaceChildren(fragment);
    }

    fetchMonth(month) {
        if (this.cache.has(month)) {
            return Promise.resolve(this.cache.get(month));
        }

        if (this.pending.has(month)) {
            return this.pending.get(month);
        }

        if (this.endpoint === '') {
            return Promise.reject(new Error('Calendar endpoint is unavailable.'));
        }

        const url = new URL(this.endpoint, window.location.origin);
        url.searchParams.set('month', month);

        const request = fetch(url.toString(), {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
        })
            .then((response) => {
                if (! response.ok) {
                    throw new Error(`Calendar request failed with ${response.status}.`);
                }

                return response.json();
            })
            .then((payload) => {
                const events = Array.isArray(payload.events) ? payload.events : [];
                this.cache.set(month, events);
                return events;
            })
            .finally(() => {
                this.pending.delete(month);
            });

        this.pending.set(month, request);
        return request;
    }

    prefetchAdjacentMonths(month) {
        [-1, 1].forEach((offset) => {
            const adjacentMonth = this.shiftMonth(month, offset);
            this.fetchMonth(adjacentMonth).catch(() => {});
        });
    }

    shiftMonth(month, offset) {
        const [year, monthNumber] = month.split('-').map(Number);
        const date = new Date(year, monthNumber - 1 + offset, 1);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    pickDefaultSelectedDate(month, eventDates) {
        if (! Array.isArray(eventDates) || eventDates.length === 0) {
            return '';
        }

        const sortedDates = [...eventDates].sort();
        const currentMonth = this.currentMonth();

        if (month !== currentMonth) {
            return sortedDates[0];
        }

        const today = this.toDateKey(new Date());

        for (const eventDate of sortedDates) {
            if (eventDate >= today) {
                return eventDate;
            }
        }

        return sortedDates[0];
    }

    renderMonth(month, events) {
        const groupedEvents = this.groupEvents(events);
        const eventDates = Array.from(groupedEvents.keys()).sort();

        if (! this.selectedDate.startsWith(`${month}-`) || ! groupedEvents.has(this.selectedDate)) {
            this.selectedDate = this.pickDefaultSelectedDate(month, eventDates);
        }

        this.renderCalendarGrid(month, groupedEvents);
        this.renderAgenda(groupedEvents.get(this.selectedDate) || []);

        if (this.monthLabel instanceof HTMLElement) {
            this.monthLabel.textContent = this.formatMonth(month);
        }

        if (this.monthTrigger instanceof HTMLButtonElement) {
            this.monthTrigger.setAttribute(
                'aria-label',
                `Jump to another month or year. Currently showing ${this.formatMonth(month)}.`
            );
        }

        if (this.grid instanceof HTMLElement) {
            this.grid.setAttribute('aria-label', this.formatMonth(month));
        }

        if (this.monthEmpty instanceof HTMLElement) {
            this.monthEmpty.classList.toggle('is-hidden', events.length > 0);
        }
    }

    groupEvents(events) {
        const grouped = new Map();

        events.forEach((event) => {
            const date = event?.event_meta?.event_date;

            if (typeof date !== 'string' || ! /^\d{4}-\d{2}-\d{2}$/.test(date)) {
                return;
            }

            if (! grouped.has(date)) {
                grouped.set(date, []);
            }

            grouped.get(date).push(event);
        });

        return grouped;
    }

    renderCalendarGrid(month, groupedEvents) {
        if (!(this.grid instanceof HTMLElement)) {
            return;
        }

        const [year, monthNumber] = month.split('-').map(Number);
        const firstDay = new Date(year, monthNumber - 1, 1);
        const daysInMonth = new Date(year, monthNumber, 0).getDate();
        const mondayOffset = (firstDay.getDay() + 6) % 7;
        const today = this.toDateKey(new Date());
        const fragment = document.createDocumentFragment();

        for (let blank = 0; blank < mondayOffset; blank += 1) {
            const spacer = document.createElement('span');
            spacer.className = 'kaul-calendar__day kaul-calendar__day--blank';
            spacer.setAttribute('aria-hidden', 'true');
            fragment.appendChild(spacer);
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
            const date = `${month}-${String(day).padStart(2, '0')}`;
            const dayEvents = groupedEvents.get(date) || [];
            const hasEvents = dayEvents.length > 0;
            const cell = document.createElement(hasEvents ? 'button' : 'span');
            const number = document.createElement('span');

            cell.className = 'kaul-calendar__day';
            number.className = 'kaul-calendar__day-number';
            number.textContent = String(day);
            cell.appendChild(number);

            if (date === today) {
                cell.classList.add('is-today');
            }

            if (hasEvents) {
                const count = document.createElement('span');
                cell.classList.add('kaul-calendar__day--event');
                cell.type = 'button';
                cell.dataset.calendarDate = date;
                cell.setAttribute('aria-pressed', date === this.selectedDate ? 'true' : 'false');
                cell.setAttribute('aria-label', `${this.formatDate(date)}, ${dayEvents.length} ${dayEvents.length === 1 ? 'event' : 'events'}`);

                if (date === this.selectedDate) {
                    cell.classList.add('is-selected');
                }

                count.className = 'kaul-calendar__event-count';
                count.setAttribute('aria-hidden', 'true');
                count.textContent = String(dayEvents.length);
                cell.appendChild(count);
            } else {
                cell.setAttribute('role', 'gridcell');
            }

            fragment.appendChild(cell);
        }

        this.grid.replaceChildren(fragment);
    }

    selectDate(date, trigger) {
        const events = this.cache.get(this.activeMonth) || [];
        const groupedEvents = this.groupEvents(events);

        if (! groupedEvents.has(date)) {
            return;
        }

        this.selectedDate = date;
        this.lastTrigger = trigger;
        this.renderCalendarGrid(this.activeMonth, groupedEvents);
        this.renderAgenda(groupedEvents.get(date) || []);

        if (this.mobileMedia.matches) {
            this.openDialog(groupedEvents.get(date) || []);
        }
    }

    renderAgenda(events) {
        if (this.agendaDate instanceof HTMLElement) {
            this.agendaDate.textContent = this.selectedDate !== ''
                ? this.formatDate(this.selectedDate)
                : this.formatMonth(this.activeMonth);
        }

        this.renderEventCards(this.agendaEvents, events);
    }

    openDialog(events) {
        if (!(this.dialog instanceof HTMLDialogElement)) {
            return;
        }

        if (this.dialogTitle instanceof HTMLElement) {
            this.dialogTitle.textContent = this.formatDate(this.selectedDate);
        }

        this.renderEventCards(this.dialogEvents, events);

        if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
        } else {
            this.dialog.setAttribute('open', '');
        }
    }

    closeDialog() {
        if (!(this.dialog instanceof HTMLDialogElement) || ! this.dialog.open) {
            return;
        }

        if (typeof this.dialog.close === 'function') {
            this.dialog.close();
        } else {
            this.dialog.removeAttribute('open');
        }
    }

    renderEventCards(container, events) {
        if (!(container instanceof HTMLElement)) {
            return;
        }

        if (events.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'kaul-calendar__empty';
            empty.textContent = this.emptyMessage;
            container.replaceChildren(empty);
            return;
        }

        const fragment = document.createDocumentFragment();
        events.forEach((event) => fragment.appendChild(this.createEventCard(event)));
        container.replaceChildren(fragment);
    }

    createEventCard(event) {
        const article = document.createElement('article');
        const meta = event?.event_meta || {};
        const title = event?.title?.rendered || 'Event';
        const excerpt = event?.excerpt?.rendered || '';

        article.className = 'kaul-calendar__event';

        if (meta.event_eyebrow) {
            article.appendChild(this.textElement('p', 'kaul-calendar__event-eyebrow', meta.event_eyebrow));
        }

        article.appendChild(this.textElement('h4', 'kaul-calendar__event-title', title));

        if (meta.event_time) {
            article.appendChild(this.textElement('p', 'kaul-calendar__event-meta', meta.event_time));
        }

        if (meta.event_location) {
            article.appendChild(this.textElement('p', 'kaul-calendar__event-meta', meta.event_location));
        }

        if (excerpt) {
            article.appendChild(this.textElement('p', 'kaul-calendar__event-summary', excerpt));
        }

        const actions = document.createElement('div');
        actions.className = 'kaul-calendar__event-actions';

        this.appendAction(
            actions,
            meta.event_primary_label,
            meta.event_primary_url,
            'kul-button kul-button--tone-primary kul-button--style-solid kul-button--size-xs'
        );
        this.appendAction(
            actions,
            meta.event_secondary_label,
            meta.event_secondary_url,
            'kul-button kul-button--tone-secondary kul-button--style-outline kul-button--size-xs'
        );

        if (actions.childElementCount === 0) {
            this.appendAction(
                actions,
                'View event',
                event?.link,
                'kul-button kul-button--tone-primary kul-button--style-solid kul-button--size-xs'
            );
        }

        article.appendChild(actions);
        return article;
    }

    appendAction(container, label, href, className) {
        const safeHref = this.safeHref(href);

        if (! label || safeHref === '') {
            return;
        }

        const link = document.createElement('a');
        link.className = className;
        link.href = safeHref;
        link.textContent = label;
        container.appendChild(link);
    }

    safeHref(href) {
        if (typeof href !== 'string' || href.trim() === '' || href.trim() === '#') {
            return '';
        }

        try {
            const url = new URL(href, window.location.origin);
            return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
        } catch (error) {
            return '';
        }
    }

    textElement(tagName, className, text) {
        const element = document.createElement(tagName);
        element.className = className;
        element.textContent = text;
        return element;
    }

    formatMonth(month) {
        const [year, monthNumber] = month.split('-').map(Number);
        return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
            month: 'long',
            year: 'numeric',
        }).format(new Date(year, monthNumber - 1, 1));
    }

    formatDate(date) {
        const [year, monthNumber, day] = date.split('-').map(Number);
        return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        }).format(new Date(year, monthNumber - 1, day));
    }

    toDateKey(date) {
        return [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0'),
        ].join('-');
    }

    setLoading(isLoading) {
        this.root.classList.toggle('is-loading', isLoading);
        this.root.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    setStatus(message) {
        if (this.status instanceof HTMLElement) {
            this.status.textContent = message;
        }
    }
}
